import { Trans, useLingui } from "@lingui/react/macro";
import { MenuIcon, XIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui-kit/base/sheet";

import logoPng from "../../../assets/sq_logo.png";
import { Button } from "../../../components/ui-kit/button";
import { AppModel } from "../../app-model";
import { pageTitles } from "../../page-titles";
import { MenuItem } from "../menu/menu-list";
import { buildMenu } from "../menu/nav-menu";
import styles from "./legacy-header.module.css";
import { LegacyMenuList } from "./legacy-menu-list";

/** The pre-redesign top bar: section links across the top, a sheet on mobile. */
export const LegacyHeader = observer(function LegacyHeader({
  app,
}: {
  app: AppModel;
}) {
  const { t, i18n } = useLingui();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Unlike the new top bar, this one has no fixed height: its links wrap onto
  // a second row once the window narrows. Publish the measured height so
  // anything pinned below the header (`--app-header-height`) follows it.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const style = document.documentElement.style;
    const observer = new ResizeObserver(() =>
      style.setProperty("--app-header-height", `${el.offsetHeight}px`),
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      style.removeProperty("--app-header-height");
    };
  }, []);

  // Close the drawer when a navigation link inside it is clicked. Caret toggles
  // aren't anchors, so clicking them doesn't match and the drawer stays open.
  const closeDrawerOnNav = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("a")) {
      setDrawerOpen(false);
    }
  };

  // The shared tree moved "Add new patient" out into the new top bar's button;
  // this layout had it as a nav link beside the patient list, so put it back.
  const [patients, ...rest] = buildMenu(app, useLocation().pathname, i18n);
  const addPatient: MenuItem = {
    to: "/add-patient",
    label: i18n._(pageTitles.addNewPatient),
    show: app.seerlinqApi.canIAddPatients,
  };
  const menu = [patients, addPatient, ...rest];

  return (
    <div
      ref={rootRef}
      data-slot="main-header"
      className={`sticky top-0 bg-background z-40 flex items-center px-4 py-2 ${styles.root}`}
    >
      <div className="flex grow items-center nav:hidden">
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger
            className="bg-transparent border-none p-3"
            aria-label={t`Open menu`}
          >
            <MenuIcon className="size-4" />
          </SheetTrigger>

          <SheetContent
            side="left"
            showClose={false}
            className="gap-0 p-0 overflow-y-auto w-[340px] max-w-[100vw]"
            onClick={closeDrawerOnNav}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--menu-divider)] bg-background px-4 py-3.5">
              <Link to="/" className={`flex items-center ${styles.logo}`}>
                <img src={logoPng} />
              </Link>
              <SheetTitle className="sr-only">
                <Trans>Navigation</Trans>
              </SheetTitle>
              <SheetClose
                aria-label={t`Close menu`}
                className="bg-transparent border-none p-1 text-muted-foreground hover:text-foreground"
              >
                <XIcon className="size-4" />
              </SheetClose>
            </div>

            <div className="px-2 pt-2 pb-4">
              <LegacyMenuList items={menu} variant="sheet" />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="hidden grow items-center nav:flex">
        <div className="shrink-0">
          <Link to="/" className={`flex items-center ${styles.logo}`}>
            <img src={logoPng} />
          </Link>
        </div>

        <ul className={`grow ${styles.nav} ${styles.navWide}`}>
          {menu
            .filter((item) => item.show !== false)
            .map((item) => (
              <li key={item.to}>
                <Link to={item.to}>{item.label}</Link>
              </li>
            ))}
        </ul>
      </div>

      <div className="flex items-center justify-end">
        <Link to="/account" className="hover:underline cursor-pointer">
          {app.seerlinqApi.username}
        </Link>

        <div className="w-4" />

        <Button
          onClick={async () => {
            await app.seerlinqApi.logout();
          }}
        >
          <Trans>Logout</Trans>
        </Button>
      </div>
    </div>
  );
});
